import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import debounce from 'lodash/debounce';
import withHostContext, {
  trackingLocations,
  trackClickTypes,
  trackClick,
} from '../../../context/host/consumer';
import Button from '../../../components/Button';
import SanitizeHTML from '../../../components/SanitizedHTML';
import './styles.css';
import messages from '../messages';
import get from 'lodash/get';
import { isVisible } from '../../../utils/isVisible';
import { cleanUpHTMLText } from '../../../utils/strings';

class Profile extends React.PureComponent {
  static propTypes = {
    profile: PropTypes.object,
    showHeading: PropTypes.bool,
    collegeName: PropTypes.string,
    intl: PropTypes.object,
  };

  trackViewOnce = true;

  constructor(props) {
    super(props);
    this.state = {
      isReadMore: false,
    };
    this.trackViewOnce = true;
  }

  componentDidMount() {
    window.addEventListener('resize', this.trackVisibleProfile);
    window.addEventListener('scroll', this.trackVisibleProfile);
    this.trackVisibleProfile();
  }

  trackVisibleProfile = debounce(() => {
    if (this.trackViewOnce && this.refs.profile) {
      if (this.trackViewOnce && isVisible(this.refs.profile)) {
        this.trackViewOnce = false;
        trackClick(this.props.hostContext, {
          type: trackClickTypes.CLICK,
          productId: this.props.profile.product_id,
          location: `${trackingLocations.TABS.CURRENT_TAB}_profile`,
        });
      }
    }
  }, 300);

  handleLinkClick = (e) => {
    if (get(e, 'target.href', null)) {
      trackClick(this.props.hostContext, {
        productId: this.props.profile.product_id,
        location: `${trackingLocations.TABS.CURRENT_TAB}_profile_link_${e.target.href}`,
      });
    }
  };

  toggleReadMore = (e) => {
    if (get(e, 'target', null) && !this.state.isReadMore) {
      trackClick(this.props.hostContext, {
        productId: e.target.getAttribute('data-click-ppid'),
        location: e.target.getAttribute('data-click-location'),
      });
    }
    this.setState(({ isReadMore }) => ({
      isReadMore: !isReadMore,
    }));
  };

  getHeadingMessages = () => {
    const { profile } = this.props;
    switch (profile && profile.type) {
      case 'student':
        return {
          headingMsg: messages.studentHeading,
          aboutMsg: messages.learnMoreAboutStudents,
        };
      case 'alumni':
        return {
          headingMsg: messages.alumniHeading,
          aboutMsg: messages.learnMoreAboutAlumni,
        };
      case 'faculty':
        return {
          headingMsg: messages.facultyHeading,
          aboutMsg: messages.learnMoreAboutFaculty,
        };
      case 'program':
        return {
          headingMsg: messages.programHeading,
          aboutMsg: messages.learnMoreAboutPrograms,
        };
      default:
        return { headingMsg: {}, aboutMsg: {} };
    }
  };

  isStudentOrAlumniProfile = () => {
    const profile = this.props.profile || {};
    return profile.type === 'student' || profile.type === 'alumni';
  };

  isProgramProfile = () => {
    const { profile = {} } = this.props;
    return profile && profile.type === 'program';
  };

  getHeading = () => {
    const { collegeName } = this.props;
    const { headingMsg, aboutMsg } = this.getHeadingMessages();

    return (
      <div
        className={`profile-heading-container common-core-width ${
          this.isProgramProfile() ? 'program' : ''
        }`}
      >
        <h2 data-cy="profile-heading" className="profile-heading" tabIndex="0">
          <FormattedMessage {...headingMsg} />
        </h2>
        <p data-cy="profile-learn-more" className="profile-learn-more" tabIndex="0">
          <FormattedMessage {...aboutMsg} values={{ college: collegeName }} />
        </p>
      </div>
    );
  };

  showInfo = () => {
    const { profile = {} } = this.props;
    if (profile && profile.type === 'student') {
      return (
        this.isValidFavActivities() ||
        profile.fav_hangout ||
        profile.best_place_to_eat ||
        profile.travel
      );
    }
    return false;
  };

  isValidFavActivities = () => {
    const { profile = {} } = this.props;
    if (get(profile, 'fav_activities.length') > 0) {
      return profile.fav_activities.find((act) => !!act);
    }
    return false;
  };

  render() {
    const { profile = {}, showHeading, intl } = this.props;
    const { isReadMore } = this.state;

    if (!profile) {
      return <React.Fragment></React.Fragment>;
    }

    return (
      <React.Fragment>
        {showHeading && this.getHeading()}
        <div
          className={`profile-main-container common-full-width ${
            isReadMore ? 'more-info' : ''
          }`}
          ref="profile"
          onClick={this.handleLinkClick}
        >
          <div className="common-core-width">
            <div className={`profile-container ${isReadMore ? 'more-info' : ''}`}>
              <div
                className="image-container"
                style={{
                  backgroundPosition: '50% 50%',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundImage: `url("${profile.img}")`,
                }}
              >
                {profile.img && (
                  <img
                    className="image"
                    src={profile.img}
                    alt={`${profile.persons_name || profile.program_name}`}
                  ></img>
                )}
              </div>
              <div className="details">
                <h4 data-cy="name" tabIndex="0" aria-label="Name" className="name">
                  {cleanUpHTMLText(profile.persons_name || profile.program_name, true)}
                </h4>

                {this.isStudentOrAlumniProfile() && (
                  <div
                    data-cy="hometown"
                    tabIndex="0"
                    aria-label="hometown"
                    className="address"
                  >
                    {cleanUpHTMLText(profile.hometown || '', true)}
                  </div>
                )}
                {this.isStudentOrAlumniProfile() && (
                  <div data-cy="major" className="major" tabIndex="0" aria-label="Major">
                    {profile.major && <span>{cleanUpHTMLText(profile.major, true)}</span>}
                    {profile.major && (
                      <span>{profile.type === 'student' ? ' Student' : ''} </span>
                    )}
                    {profile.class && (
                      <span>{profile.type === 'alumni' ? `- ${profile.class}` : ''}</span>
                    )}
                  </div>
                )}

                <div>
                  {profile.quote && (
                    <div
                      data-cy="quote"
                      className="quote"
                      tabIndex="0"
                      aria-label="Quote"
                    >
                      {'“'}
                      <SanitizeHTML html={cleanUpHTMLText(profile.quote, true)} />
                      {'“'}
                    </div>
                  )}
                </div>
                {!isReadMore && (
                  <Button
                    tabIndex="0"
                    data-cy="read-more-btn"
                    className="read-more-btn"
                    onClick={this.toggleReadMore}
                    aria-label="Read More information"
                    data-click-ppid={profile.product_id}
                    data-click-location={trackingLocations.PROFILES}
                  >
                    <FormattedMessage {...messages.readMoreBtn} />
                  </Button>
                )}
              </div>

              {isReadMore && (
                <div
                  className={`more-info-container ${this.showInfo() ? '' : 'body-only'}`}
                >
                  <div className="more-info-body">
                    {profile.type !== 'student' && profile.type !== 'school' && (
                      <div data-cy="more-info-heading" className="more-info-body-heading">
                        {profile.type === 'alumni' && (
                          <FormattedMessage {...messages.alumniMore} />
                        )}
                        {profile.type === 'faculty' && (
                          <FormattedMessage {...messages.facultyMore} />
                        )}
                        {profile.type === 'program' && (
                          <FormattedMessage {...messages.programMore} />
                        )}
                      </div>
                    )}
                    <div
                      data-cy="more-info-body-content"
                      className="more-info-body-content"
                      tabIndex="0"
                      aria-label="more information"
                    >
                      <SanitizeHTML html={cleanUpHTMLText(profile.body)} />
                    </div>
                  </div>

                  {this.showInfo() && (
                    <div className="info">
                      {this.isValidFavActivities() && (
                        <div className="info-container">
                          <div
                            data-cy="fav-campus-activities-heading"
                            className="info-heading"
                          >
                            <FormattedMessage {...messages.favCampusActivities} />
                          </div>
                          <div
                            data-cy="fav-campus-activities-content"
                            className="info-content"
                            tabIndex="0"
                            aria-label={intl.formatMessage(messages.favCampusActivities)}
                          >
                            {profile.fav_activities.map(
                              (act, idx) =>
                                act && (
                                  <div tabIndex="0" key={`${act}-${idx}`}>
                                    {cleanUpHTMLText(act, true)}
                                  </div>
                                ),
                            )}
                          </div>
                        </div>
                      )}

                      {profile.fav_hangout && (
                        <div className="info-container">
                          <div
                            data-cy="fav-campus-hangout-heading"
                            className="info-heading"
                          >
                            <FormattedMessage {...messages.favCampusHangout} />
                          </div>
                          <div
                            data-cy="fav-campus-hangout-content"
                            className="info-content"
                            tabIndex="0"
                            aria-label={intl.formatMessage(messages.favCampusHangout)}
                          >
                            {cleanUpHTMLText(profile.fav_hangout, true)}
                          </div>
                        </div>
                      )}

                      {profile.best_place_to_eat && (
                        <div className="info-container">
                          <div data-cy="fav-placetoeat-heading" className="info-heading">
                            <FormattedMessage {...messages.favPlaceToEat} />
                          </div>
                          <div
                            data-cy="fav-placetoeat-content"
                            className="info-content"
                            tabIndex="0"
                            aria-label={intl.formatMessage(messages.favPlaceToEat)}
                          >
                            {cleanUpHTMLText(profile.best_place_to_eat, true)}
                          </div>
                        </div>
                      )}

                      {profile.travel && (
                        <div className="info-container">
                          <div data-cy="fav-travel-heading" className="info-heading">
                            <FormattedMessage {...messages.travel} />
                          </div>
                          <div
                            data-cy="fav-travel-content"
                            className="info-content"
                            tabIndex="0"
                            aria-label={intl.formatMessage(messages.travel)}
                          >
                            {cleanUpHTMLText(profile.travel, true)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isReadMore && (
                <Button
                  data-cy="close-more-info-btn"
                  className="close-more-info-btn"
                  aria-label="close more information"
                  onClick={this.toggleReadMore}
                  icon="close"
                ></Button>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default injectIntl(withHostContext(Profile));
